import { useState } from 'react';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';

import { formatarValorEmReais } from '../../../shared/dominio/moeda';
import { EstadoDeCarregamento } from '../../../shared/ui/EstadoDeCarregamento';
import { EstadoDeErro } from '../../../shared/ui/EstadoDeErro';
import { EstadoVazio } from '../../../shared/ui/EstadoVazio';
import { opcoesDaComparacao } from '../api/contratos';
import { ROTULOS_DE_DIVERGENCIA, nomeDoContratoComUf, type Contrato } from '../api/tipos';
import { PaginacaoDaTabela } from './PaginacaoDaTabela';
import { useBuscaPaginada } from './useBuscaPaginada';
import { competenciaDe } from '../../../shared/dominio/datas';

/**
 * Comparação de dois contratos-irmãos (`TP-049`, S4): mesmo código, valor ou
 * unidade diferentes, ou presente em só um dos dois lados.
 */
export function ComparacaoDeContratos({ contratos }: { contratos: readonly Contrato[] }): React.JSX.Element {
  const [a, definirA] = useState('');
  const [b, definirB] = useState('');
  const [competencia, definirCompetencia] = useState(competenciaDe());

  const filtro = useBuscaPaginada();

  const consulta = useQuery(opcoesDaComparacao(a, b, competencia, filtro.busca, filtro.pagina + 1, filtro.tamanho));
  const prontaParaComparar = a !== '' && b !== '' && a !== b;

  return (
    <Stack spacing={1.5}>
      <Typography variant="h3">Comparação de contratos</Typography>
      <Typography variant="body2" color="text.secondary">
        Compara os itens de dois contratos numa competência — a divergência que a Anery descobre antes da operadora.
      </Typography>

      <Stack direction="row" spacing={1.5}>
        <TextField
          select
          label="Primeiro contrato"
          size="small"
          value={a}
          onChange={(evento) => {
            definirA(evento.target.value);
            filtro.voltarParaPrimeiraPagina();
          }}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">Escolha um contrato</MenuItem>
          {contratos.map((contrato) => (
            <MenuItem key={contrato.id} value={contrato.id}>
              {nomeDoContratoComUf(contrato)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Segundo contrato"
          size="small"
          value={b}
          onChange={(evento) => {
            definirB(evento.target.value);
            filtro.voltarParaPrimeiraPagina();
          }}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">Escolha um contrato</MenuItem>
          {contratos.map((contrato) => (
            <MenuItem key={contrato.id} value={contrato.id}>
              {nomeDoContratoComUf(contrato)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Competência"
          type="month"
          size="small"
          value={competencia}
          onChange={(evento) => {
            definirCompetencia(evento.target.value);
            filtro.voltarParaPrimeiraPagina();
          }}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          label="Buscar por código ou descrição"
          size="small"
          value={filtro.digitado}
          onChange={(evento) => {
            filtro.definirDigitado(evento.target.value);
          }}
          helperText={filtro.aviso}
          sx={{ minWidth: 260 }}
        />
      </Stack>

      {a !== '' && a === b ? (
        <Typography color="error.main" variant="body2">
          Escolha dois contratos diferentes.
        </Typography>
      ) : null}

      {!prontaParaComparar && !(a !== '' && a === b) ? (
        <Typography variant="body2" color="text.secondary">
          Escolha os dois contratos para comparar.
        </Typography>
      ) : null}

      {prontaParaComparar && consulta.isPending ? <EstadoDeCarregamento linhas={3} /> : null}

      {prontaParaComparar && consulta.isError ? (
        <EstadoDeErro
          erro={consulta.error}
          contexto="Não foi possível comparar os contratos"
          aoReprocessar={() => {
            void consulta.refetch();
          }}
        />
      ) : null}

      {prontaParaComparar && consulta.isSuccess && consulta.data.total === 0 ? (
        <EstadoVazio
          titulo="Nenhuma divergência"
          descricao={
            filtro.busca !== ''
              ? 'Nenhuma divergência combina com a busca.'
              : `Em ${competencia.slice(5)}/${competencia.slice(0, 4)}, os dois contratos têm os mesmos itens, valores e unidades. Troque a competência para ver outro mês.`
          }
        />
      ) : null}

      {prontaParaComparar && consulta.isSuccess && consulta.data.total > 0 ? (
        <TableContainer>
          <Table aria-label="Divergências entre os contratos" size="small">
            <TableHead>
              <TableRow>
                <TableCell>Código</TableCell>
                <TableCell>Descrição</TableCell>
                <TableCell>Divergência</TableCell>
                <TableCell>Valor — primeiro</TableCell>
                <TableCell>Valor — segundo</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {consulta.data.divergencias.map((divergencia) => (
                <TableRow key={divergencia.codigo} hover>
                  <TableCell>{divergencia.codigo}</TableCell>
                  <TableCell>{(divergencia.itemA ?? divergencia.itemB)?.descricao ?? '—'}</TableCell>
                  <TableCell>{ROTULOS_DE_DIVERGENCIA[divergencia.motivo]}</TableCell>
                  <TableCell>
                    {divergencia.itemA !== null ? formatarValorEmReais(divergencia.itemA.valor) : '—'}
                  </TableCell>
                  <TableCell>
                    {divergencia.itemB !== null ? formatarValorEmReais(divergencia.itemB.valor) : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <PaginacaoDaTabela total={consulta.data.total} busca={filtro} />
        </TableContainer>
      ) : null}
    </Stack>
  );
}
